import {
  Card,
  CardAction,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { LucideIcon } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { getTranslatedName as utilsGetTranslatedName } from "@/lib/utils";

export default function ServiceCard(service: any) {
  const navigate = useNavigate();

  const { t, i18n } = useTranslation();

  function toPascalCase(value: string) {
    return value
      .split(/[-_\s]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join("");
  }

  function normalizeBase64Image(value: unknown): string | undefined {
    if (typeof value !== "string") return undefined;
    const trimmed = value.trim();
    if (!trimmed) return undefined;

    if (
      trimmed.startsWith("data:image/") ||
      trimmed.startsWith("http://") ||
      trimmed.startsWith("https://") ||
      trimmed.startsWith("/")
    ) {
      return trimmed;
    }

    if (/^[A-Za-z0-9+/=\r\n]+$/.test(trimmed)) {
      return `data:image/png;base64,${trimmed.replace(/\s+/g, "")}`;
    }

    return undefined;
  }

  const customIconSrc = normalizeBase64Image(service?.customIcon);
  const rawIconName = service?.icon || service?.defaultIcon;

  const iconByName =
    typeof rawIconName === "string" && rawIconName.trim()
      ? (LucideIcons[toPascalCase(rawIconName) as keyof typeof LucideIcons] as
          | LucideIcon
          | undefined)
      : undefined;

  const ServiceIcon =
    typeof service?.icon === "function"
      ? service.icon
      : iconByName || (rawIconName ? LucideIcons.ConciergeBell : undefined);

  function getLocalizedCover(
    cover: unknown,
    currentLang: string,
  ): string | undefined {
    if (!cover) return undefined;
    if (typeof cover === "object" && cover !== null) {
      const langKey = currentLang === "en" ? "gb" : currentLang;
      const val =
        (cover as any)[langKey] ||
        (cover as any)[currentLang] ||
        (cover as any)["it"] ||
        Object.values(cover)[0];
      return typeof val === "string" && val.trim() ? val.trim() : undefined;
    }
    if (typeof cover !== "string" || !cover.trim()) return undefined;
    const trimmed = cover.trim();
    if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === "object") {
          const langKey = currentLang === "en" ? "gb" : currentLang;
          const val =
            parsed[langKey] ||
            parsed[currentLang] ||
            parsed["it"] ||
            Object.values(parsed)[0];
          return typeof val === "string" && val.trim() ? val.trim() : undefined;
        }
      } catch {
        return undefined;
      }
    }
    return trimmed;
  }

  const activeCover =
    getLocalizedCover(service?.cover, i18n.language) ||
    (Array.isArray(service?.serviceImages) && service.serviceImages.length > 0
      ? service.serviceImages[0]
      : undefined);

  const isInformative = Number(service?.serviceType) === 2;

  function getTranslatedName(value: any): string {
    return utilsGetTranslatedName(value, i18n.language);
  }

  return (
    <>
      <Card
        className="relative mx-auto w-full max-w-sm pt-0 h-96 justify-between overflow-hidden cursor-pointer"
        onClick={() => navigate(`/services/${service.id}`)}
      >
        {activeCover ? (
          <div className="relative aspect-video w-full rounded-t-xl overflow-hidden">
            <div className="absolute inset-0 z-30 bg-black/35" />
            <img
              src={activeCover}
              alt="Event cover"
              className="relative z-20 aspect-video w-full object-cover brightness-60 dark:brightness-40"
            />
          </div>
        ) : (
          <div className="relative z-20 aspect-video w-full rounded-t-xl bg-zinc-300 dark:bg-zinc-900 flex items-center justify-center overflow-hidden">
            {customIconSrc ? (
              <img
                src={customIconSrc}
                alt="Service icon"
                className="w-24 h-24 object-contain brightness-0 invert dark:brightness-0 dark:opacity-25"
              />
            ) : ServiceIcon ? (
              <ServiceIcon className="w-24 h-24 text-white dark:text-zinc-800 stroke-[1.25]" />
            ) : null}
          </div>
        )}

        <CardHeader>
          <CardAction>
            {/* <Badge variant="secondary">Featured</Badge> */}
          </CardAction>
          <CardTitle className="flex gap-2 items-center">
            {customIconSrc ? (
              <img src={customIconSrc} alt="Custom icon" className="w-6 h-6" />
            ) : ServiceIcon ? (
              <ServiceIcon className="w-6 h-6" />
            ) : (
              <LucideIcons.ConciergeBell className="w-6 h-6" />
            )}
            {getTranslatedName(service.name)}
          </CardTitle>
          <CardDescription className="">
            {(() => {
              const desc = getTranslatedName(service.description)
                ?.replace(/<[^>]+>/g, "")
                .replace(/&nbsp;|\u00A0/g, " ")
                .trim();
              return desc ? desc.substring(0, 80) + "..." : "";
            })()}
          </CardDescription>
        </CardHeader>
        <CardFooter>
          <Button type="button" className="w-full">
            {isInformative
              ? t("services.discoverSingleAction")
              : t("services.requestSingleAction")}
            <LucideIcons.ArrowRight />
          </Button>
        </CardFooter>
      </Card>
    </>
  );
}
